import { apiClient, ApiError } from '../client';
import { API_ENDPOINTS } from '../config';
import { getOrganizationId } from '../organization';
import { getAiAnalyticsUrl } from '../aiAnalyticsUrl';

export interface Template {
  id: string;
  name: string;
  category: string;
  language: string;
  status: 'approved' | 'pending' | 'rejected' | 'draft';
  business_account_id: string;
  whatsapp_template_id?: string;
  meta_response?: Record<string, any>;
  parent_template_id?: string;
  version: string;
  created_by?: string;
  updated_by?: string;
  submitted_at?: string;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
  createdAt?: string;
  updatedAt?: string;
  businessAccountId?: string;
  whatsappTemplateId?: string;
  metaResponse?: Record<string, any>;
  parentTemplateId?: string;
  createdBy?: string;
  updatedBy?: string;
  submittedAt?: string;
  deletedAt?: string;
}

export interface CreateTemplateData {
  businessAccountId: string;
  name: string;
  category: string;
  language: string;
  components: any[];
  userId?: string;
  templateType?: 'TEXT_ONLY' | 'TEXT_WITH_BUTTONS' | 'IMAGE_HEADER' | 'IMAGE_WITH_BUTTONS' | 'VIDEO_HEADER' | 'VIDEO_WITH_BUTTONS' | 'DOCUMENT_HEADER' | 'OTP_TEMPLATE' | 'OTP_WITH_BUTTONS' | 'CAROUSEL';
  mediaFiles?: Array<{
    url: string;
    type: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
    componentIndex: number;
  }>;
}

export interface SendTemplateData {
  businessAccountId: string;
  to: string;
  templateName: string;
  language: string;
  parameters?: string[];
  conversationId?: string | null;
  contactId?: string | null;
}

export interface ImportTemplatesData {
  businessAccountId: string;
}

export interface SyncTemplatesData {
  businessAccountId: string;
}

export const templatesApi = {
  normalizeGeneratedTemplateText(text: string) {
    return text
      .replace(/\{\{\{/g, '{{')
      .replace(/\}\}\}/g, '}}')
      .replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_match, inner) => `{{${String(inner).trim()}}}`);
  },

  async getAll(businessAccountId?: string) {
    const params = businessAccountId ? { businessAccountId: businessAccountId } : {};
    return apiClient.get(API_ENDPOINTS.TEMPLATES, { params });
  },

  /** One server page of the workspace's templates; `pagination.total` counts every match. */
  async getPaged(params: {
    businessAccountId: string;
    limit: number;
    offset: number;
    search?: string;
    statuses?: string;
    categories?: string;
    createdFrom?: string;
    createdTo?: string;
    /** Only `pagination.total` comes back - no rows. */
    totalOnly?: boolean;
  }) {
    const query: Record<string, string | number> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '' || value === false) return;
      query[key] = typeof value === 'boolean' ? String(value) : value;
    });
    return apiClient.get(API_ENDPOINTS.TEMPLATES, { params: query });
  },

  async getTypes() {
    return apiClient.get('/templates/types');
  },

  async getById(id: string) {
    return apiClient.get(`${API_ENDPOINTS.TEMPLATES}/${id}`);
  },

  async create(data: CreateTemplateData) {
    return apiClient.post(API_ENDPOINTS.TEMPLATES, data);
  },

  async delete(id: string) {
    return apiClient.delete(`${API_ENDPOINTS.TEMPLATES}/${id}`);
  },

  async sendTemplate(data: SendTemplateData) {
    return apiClient.post('/whatsapp/send-template', data);
  },

  async importTemplates(data: ImportTemplatesData) {
    return apiClient.post('/whatsapp/import-templates', data);
  },

  async syncTemplates(data: SyncTemplatesData) {
    return apiClient.post('/templates/sync', data);
  },

  async previewTemplate(id: string, variables?: Record<string, string>) {
    return apiClient.post(`/templates/${id}/preview`, { variables });
  },

  async generateAITemplates(data: {
    category: string;
    specifications: string;
    language: string;
    businessAccountId?: string | null;
    headerType?: string;
    includeMedia?: boolean;
    includeButtons?: boolean;
    image?: File | null;
  }) {
    const organizationId = getOrganizationId();
    const token = localStorage.getItem('auth_token');

    let specifications = data.specifications;
    if (organizationId) {
      try {
        const promptRes = await fetch(getAiAnalyticsUrl('/start-flow/generate-prompt'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify({ organization_id: organizationId, type: 'template' }),
        });
        if (promptRes.ok) {
          const promptData = await promptRes.json();
          if (promptData?.prompt) {
            specifications = promptData.prompt;
          }
        }
      } catch {
      }
    }

    const formData = new FormData();
    formData.append('specifications', specifications);
    formData.append('language', data.language);
    if (['AUTHENTICATION', 'MARKETING', 'UTILITY'].includes(data.category)) {
      formData.append('category', data.category);
    }
    if (organizationId) {
      formData.append('organization_id', organizationId);
    }
    if (data.businessAccountId) {
      formData.append('business_account_id', data.businessAccountId);
    }
    formData.append('header_type', data.headerType || (data.image ? 'IMAGE' : 'TEXT'));
    formData.append('include_media', String(data.includeMedia ?? Boolean(data.image)));
    formData.append('include_buttons', String(data.includeButtons ?? true));
    if (data.image) {
      formData.append('image', data.image, data.image.name);
    }

    const response = await fetch(getAiAnalyticsUrl('/generate-templates'), {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detailMessage =
        typeof errorData?.detail === 'string'
          ? errorData.detail
          : (errorData?.error ?? errorData?.message);
      throw ApiError.fromResult(
        { ...errorData, error: detailMessage, statusCode: response.status },
        `HTTP ${response.status}`,
      );
    }

    const json = await response.json();

    if (json?.templates && Array.isArray(json.templates)) {
      json.templates = json.templates.map((template: any) => {
        if (!template?.components || !Array.isArray(template.components)) return template;

        return {
          ...template,
          components: template.components.map((component: any) => {
            if (typeof component?.text !== 'string') return component;

            return {
              ...component,
              text: templatesApi.normalizeGeneratedTemplateText(component.text),
            };
          }),
        };
      });
    }

    return json;
  },
};
